import * as THREE from "three";
import earcut from "earcut";
import Delaunator from "delaunator";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry.js";
import { ParametricGeometry } from "three/examples/jsm/geometries/ParametricGeometry.js";
import { NURBSSurface } from "three/examples/jsm/curves/NURBSSurface.js";

export function curveToPoints(curve, pointLimit) {
  let pointCount = pointLimit;
  let rc = [];
  const ts = [];

  if (curve instanceof window.rhino.LineCurve) {
    return [curve.pointAtStart, curve.pointAtEnd];
  }

  if (curve instanceof window.rhino.PolylineCurve) {
    pointCount = curve.pointCount;
    for (let i = 0; i < pointCount; i++) {
      rc.push(curve.point(i));
    }

    return rc;
  }

  if (curve instanceof window.rhino.PolyCurve) {
    const segmentCount = curve.segmentCount;

    for (let i = 0; i < segmentCount; i++) {
      const segment = curve.segmentCurve(i);
      const segmentArray = curveToPoints(segment, pointCount);
      rc = rc.concat(segmentArray);
      segment.delete();
    }

    return rc;
  }

  if (curve instanceof window.rhino.ArcCurve) {
    pointCount = Math.floor(curve.angleDegrees / 5);
    pointCount = pointCount < 2 ? 2 : pointCount;
    // alternative to this hardcoded version: https://stackoverflow.com/a/18499923/2179399
  }

  if (curve instanceof window.rhino.NurbsCurve && curve.degree === 1) {
    const pLine = curve.tryGetPolyline();

    for (let i = 0; i < pLine.count; i++) {
      rc.push(pLine.get(i));
    }

    pLine.delete();

    return rc;
  }

  const domain = curve.domain;
  const divisions = pointCount - 1.0;

  for (let j = 0; j < pointCount; j++) {
    const t = domain[0] + (j / divisions) * (domain[1] - domain[0]);

    if (t === domain[0] || t === domain[1]) {
      ts.push(t);
      continue;
    }

    const tan = curve.tangentAt(t);
    const prevTan = curve.tangentAt(ts.slice(-1)[0]);

    // Duplicated from THREE.Vector3
    // How to pass imports to worker?

    const tS = tan[0] * tan[0] + tan[1] * tan[1] + tan[2] * tan[2];
    const ptS =
      prevTan[0] * prevTan[0] +
      prevTan[1] * prevTan[1] +
      prevTan[2] * prevTan[2];

    const denominator = Math.sqrt(tS * ptS);

    let angle;

    if (denominator === 0) {
      angle = Math.PI / 2;
    } else {
      const theta =
        (tan.x * prevTan.x + tan.y * prevTan.y + tan.z * prevTan.z) /
        denominator;
      angle = Math.acos(Math.max(-1, Math.min(1, theta)));
    }

    if (angle < 0.1) continue;

    ts.push(t);
  }

  rc = ts.map((t) => curve.pointAt(t));
  return rc;
}

function getSurfacePoint(surface, u, v, target) {
  const point = surface.pointAt(u, v);
  target.set(point[0], point[1], point[2]);
}

export function faceToGeometry(face) {
  face.setDomain(0, [0, 1]);
  face.setDomain(1, [0, 1]);
  return new ParametricGeometry(
    (u, v, target) => getSurfacePoint(face, u, v, target),
    30,
    30
  );
}

export function brepToGeometry(brep) {
  const faces = brep.faces();

  const geometries = [];

  for (let i = 0; i < faces.count; i++) {
    const face = faces.get(i);
    const geometry = faceToGeometry(face);
    geometries.push(geometry);
  }

  return geometries;
}
